// frontend/src/components/tickets/Tickets.js
// JIRA ticket tracker: list, filter, add, edit, status flow, comments, export.
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, IconButton, Tooltip, Chip, Alert, Grid,
    TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TablePagination, Dialog, DialogTitle, DialogContent, Divider,
    Skeleton, Stack, InputAdornment, Menu, LinearProgress, CircularProgress,
} from '@mui/material';
import {
    Add, Refresh, Search, Clear, Edit, Timeline, Comment, TableChart, Cancel,
    Upload,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/AuthContext';
import useReferenceData from '../../hooks/useReferenceData';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import {
    getTickets, downloadTickets, downloadTicketsTemplate, importTickets,
    setTicketStatus, decideTicket,
    getTicketHistory, getTicket, getTicketComments, addTicketComment,
    getTicketParts,
} from '../../services/endpoints';
import {
    statusChipColor, priorityChipColor, fmtDate, fmtTatDays, isManagerRole,
} from '../../constants/domain';
import TicketForm from './TicketForm';
import { parseHours } from '../parts/PartForm';
import StatusFlowPanel from '../common/StatusFlowPanel';
import CommentThread from '../common/CommentThread';

const EMPTY_FILTERS = {
    search: '', status: '', task_type: '', type_of_activity: '',
    priority: '', engineer: '', customer: '',
};

const Tickets = () => {
    const { user } = useAuth();
    const ref = useReferenceData();
    const manager = isManagerRole(user?.role);
    const [searchParams, setSearchParams] = useSearchParams();

    // Seed filters from the URL so the dashboard can deep-link into a view
    // (e.g. the Task type summary's ticket-count badges).
    const initialFilters = useMemo(() => {
        const seeded = { ...EMPTY_FILTERS };
        for (const key of Object.keys(EMPTY_FILTERS)) {
            const value = searchParams.get(key);
            if (value) seeded[key] = value;
        }
        return seeded;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [tickets, setTickets] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [applied, setApplied] = useState(initialFilters);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [selected, setSelected] = useState(null);
    const [dialog, setDialog] = useState(null); // 'add' | 'edit' | 'flow' | 'comments'
    const [exportMenu, setExportMenu] = useState(null);
    const [importing, setImporting] = useState(false);

    const fetchTickets = useCallback(async () => {
        try {
            const res = await getTickets(applied);
            setTickets(res.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    }, [applied]);

    const { lastRefresh, refreshing, refresh } = useAutoRefresh(fetchTickets);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    const applyFilters = () => {
        setApplied(filters);
        setPage(0);
        // Keep the URL in step so the view is shareable/deep-linkable.
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
        setSearchParams(params, { replace: true });
    };
    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        setPage(0);
        setSearchParams(new URLSearchParams(), { replace: true });
    };
    const activeFilterCount = Object.values(applied).filter(Boolean).length;

    const openDialog = async (kind, ticket) => {
        if (kind === 'flow' && ticket) {
            // Fetch so allowed_next is present.
            try {
                const res = await getTicket(ticket.id);
                setSelected(res.data);
            } catch {
                setSelected(ticket);
            }
        } else {
            setSelected(ticket || null);
        }
        setDialog(kind);
    };

    const closeDialog = () => { setDialog(null); setSelected(null); };

    // Tickets are never deleted once created -- only cancelled, manager-only.
    const handleCancel = async (ticket) => {
        const comment = window.prompt(`Cancel ticket ${ticket.ticket_key}. Reason (optional):`, '');
        if (comment === null) return; // user dismissed the prompt

        // Hours spent in the current stage are required for every move except
        // leaving "Yet to Start" -- nothing's been worked on yet there.
        let actualHours;
        if (ticket.status !== 'Yet to Start') {
            const input = window.prompt('Actual hours spent in the current stage (e.g. 2 or 02:30):', '');
            if (input === null) return;
            actualHours = parseHours(input);
            if (actualHours === null) {
                toast.error('Enter a valid number of hours (e.g. 2 or 02:30)');
                return;
            }
        }

        try {
            await setTicketStatus(ticket.id, { status: 'Cancelled', comment, actual_hours: actualHours });
            toast.success(`Ticket ${ticket.ticket_key} cancelled`);
            fetchTickets();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        }
    };

    const handleDownloadTemplate = async () => {
        setExportMenu(null);
        try {
            await downloadTicketsTemplate();
            toast.success('Bulk upload template downloaded');
        } catch (err) {
            toast.error(`Download failed: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';           // allow re-picking the same file
        if (!file) return;

        setImporting(true);
        try {
            const res = await importTickets(file);
            const { created, updated, skipped, errors } = res.data;
            toast.success(`Imported: ${created} created, ${updated} updated, ${skipped} skipped`);
            if (errors?.length) {
                toast.warning(`${errors.length} row(s) failed - see console for detail`);
                console.warn('Import errors:', errors);
            }
            fetchTickets();
        } catch (err) {
            toast.error(`Import failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setImporting(false);
        }
    };

    const paged = tickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box>
            {/* Header */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                mb: 2, flexWrap: 'wrap', gap: 1,
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>JIRA Tickets</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
                        {activeFilterCount > 0 && ` (filtered)`}
                        {lastRefresh && ` — updated ${lastRefresh.toLocaleTimeString('en-GB')}`}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={refresh} disabled={refreshing}><Refresh /></IconButton>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<TableChart />}
                        onClick={(e) => setExportMenu(e.currentTarget)}>
                        Export
                    </Button>
                    <Menu anchorEl={exportMenu} open={!!exportMenu} onClose={() => setExportMenu(null)}>
                        <MenuItem onClick={() => {
                            setExportMenu(null);
                            downloadTickets(applied)
                                .then(() => toast.success('Tickets exported'))
                                .catch(e => toast.error(e.message));
                        }}>
                            Export tickets (.xlsx)
                        </MenuItem>
                        {manager && (
                            <MenuItem onClick={handleDownloadTemplate}>
                                Bulk upload template (.xlsx)
                            </MenuItem>
                        )}
                    </Menu>
                    {manager && (
                        <Button component="label" variant="outlined" disabled={importing}
                            startIcon={importing ? <CircularProgress size={16} /> : <Upload />}>
                            {importing ? 'Importing...' : 'Import'}
                            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} />
                        </Button>
                    )}
                    <Button variant="contained" startIcon={<Add />}
                        onClick={() => openDialog('add')}>
                        Add JIRA ticket
                    </Button>
                </Stack>
            </Box>
            {importing && <LinearProgress sx={{ mb: 2 }} />}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" label="Search"
                            value={filters.search}
                            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                                ),
                            }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField select fullWidth size="small" label="Status"
                            value={filters.status}
                            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                            <MenuItem value="">All</MenuItem>
                            {ref.ticketStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                        <TextField select fullWidth size="small" label="Task type"
                            value={filters.task_type}
                            onChange={(e) => setFilters(f => ({ ...f, task_type: e.target.value }))}>
                            <MenuItem value="">All</MenuItem>
                            {ref.taskTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField select fullWidth size="small" label="Activity"
                            value={filters.type_of_activity}
                            onChange={(e) => setFilters(f => ({ ...f, type_of_activity: e.target.value }))}>
                            <MenuItem value="">All</MenuItem>
                            {ref.activityTypes.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                        <TextField select fullWidth size="small" label="Priority"
                            value={filters.priority}
                            onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}>
                            <MenuItem value="">All</MenuItem>
                            {ref.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField select fullWidth size="small" label="Engineer"
                            value={filters.engineer}
                            onChange={(e) => setFilters(f => ({ ...f, engineer: e.target.value }))}>
                            <MenuItem value="">All</MenuItem>
                            {ref.engineers.map(u => (
                                <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" variant="contained" onClick={applyFilters}>
                                Apply
                            </Button>
                            <Button size="small" startIcon={<Clear />} onClick={clearFilters}
                                disabled={activeFilterCount === 0}>
                                Clear
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <Paper>
                <TableContainer sx={{ maxHeight: 620 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {['Task', 'Task type', 'Activity', 'Customer', 'Engineer',
                                  'Status', 'Priority', 'Target Date', 'Delivery Date', 'Loop', 'TAT (days)', 'No of parts', ''].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {refreshing && tickets.length === 0 && [...Array(5)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell colSpan={13}><Skeleton height={28} /></TableCell>
                                </TableRow>
                            ))}
                            {!refreshing && tickets.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 5 }}>
                                        <Typography color="text.secondary">
                                            No tickets yet. Use "Add JIRA ticket" to create one.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {paged.map(t => (
                                <TableRow key={t.id} hover>
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {t.ticket_key}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={t.task_type} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 180 }}>
                                        <Typography variant="body2" noWrap>
                                            {t.type_of_activity || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{t.customer || '-'}</TableCell>
                                    <TableCell>{t.assigned_to_name || '-'}</TableCell>
                                    <TableCell>
                                        <Chip label={t.status} size="small"
                                            color={statusChipColor(t.status)} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={t.priority} size="small"
                                            color={priorityChipColor(t.priority)} variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{
                                        whiteSpace: 'nowrap',
                                        color: t.is_overdue ? 'error.main' : undefined,
                                        fontWeight: t.is_overdue ? 700 : 400,
                                    }}>
                                        {fmtDate(t.due_date) || '-'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {fmtDate(t.delivery_date) || '-'}
                                    </TableCell>
                                    <TableCell align="center">{t.loop_count || 0}</TableCell>
                                    <TableCell align="center">{fmtTatDays(t.tat_hours) ?? '-'}</TableCell>
                                    <TableCell align="center">
                                        {t.parts_count > 0 ? (
                                            <Tooltip title="View linked parts">
                                                <Chip
                                                    label={t.parts_count}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => openDialog('flow', t)}
                                                />
                                            </Tooltip>
                                        ) : 0}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Status flow">
                                            <IconButton size="small"
                                                onClick={() => openDialog('flow', t)}>
                                                <Timeline fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Comments">
                                            <IconButton size="small"
                                                onClick={() => openDialog('comments', t)}>
                                                <Comment fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton size="small"
                                                onClick={() => openDialog('edit', t)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {manager && !['Completed', 'Cancelled'].includes(t.status) && (
                                            <Tooltip title="Cancel ticket">
                                                <IconButton size="small" color="error"
                                                    onClick={() => handleCancel(t)}>
                                                    <Cancel fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={tickets.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>

            {/* Dialogs */}
            <Dialog open={dialog === 'add' || dialog === 'edit'} onClose={closeDialog}
                maxWidth="md" fullWidth>
                <DialogTitle>
                    {dialog === 'edit' ? `Edit ${selected?.ticket_key}` : 'Add JIRA ticket'}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <TicketForm
                        ticket={dialog === 'edit' ? selected : null}
                        onSuccess={() => { closeDialog(); fetchTickets(); }}
                        onCancel={closeDialog}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === 'flow'} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Status flow — {selected?.ticket_key}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {selected && (
                        <StatusFlowPanel
                            item={selected}
                            label="Ticket"
                            onSetStatus={(body) => setTicketStatus(selected.id, body)}
                            onDecide={(body) => decideTicket(selected.id, body)}
                            onLoadHistory={() => getTicketHistory(selected.id).then(r => r.data)}
                            onLoadLinkedParts={() => getTicketParts(selected.id).then(r => r.data)}
                            onDone={async () => {
                                const res = await getTicket(selected.id);
                                setSelected(res.data);
                                fetchTickets();
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === 'comments'} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Comments — {selected?.ticket_key}</DialogTitle>
                <Divider />
                <DialogContent>
                    {selected && (
                        <CommentThread
                            onLoad={() => getTicketComments(selected.id).then(r => r.data)}
                            onAdd={(text) => addTicketComment(selected.id, text)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Tickets;
